import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider
} from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon } from '@mui/icons-material';
import invoiceService from '../services/invoiceService';
import serviceService from '../../services/services/serviceService';
import QRCode from '../../../components/common/QRCode';

const SellerBlock = () => {
  return (
    <Box>
      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>ΞΑΝΘΟΥ ΧΡΗΣΤΟΣ</Typography>
      <Typography sx={{ fontSize: 11 }}>ΑΦΜ: 148060098 ΔΟΥ: ΡΟΔΟΥ</Typography>
      <Typography sx={{ fontSize: 11 }}>ΥΠΗΡΕΣΙΕΣ ΠΛΗΡΟΦΟΡΙΚΗΣ</Typography>
      <Typography sx={{ fontSize: 11 }}>ΔΗΜΟΚΡΙΤΟΥ 20 ΤΚ:85101</Typography>
      <Typography sx={{ fontSize: 11 }}>ΤΗΛ: +30 6942624846</Typography>
      <Typography sx={{ fontSize: 11 }}>Email: info@netgen.gr</Typography>
    </Box>
  );
};

const Row = ({ left, right, bold }) => (
  <Box display="flex" justifyContent="space-between" py={0.5}>
    <Typography sx={{ fontSize: 11, fontWeight: bold ? 700 : 400 }}>{left}</Typography>
    <Typography sx={{ fontSize: 11, fontWeight: bold ? 700 : 400 }}>{right}</Typography>
  </Box>
);

const TableCellBox = ({ children, align = 'left', borderTop = true, width }) => (
  <Box
    sx={{
      padding: '10px 12px',
      borderTop: borderTop ? '1px solid #dee2e6' : 'none',
      textAlign: align,
      width,
      flex: width ? '0 0 auto' : '1 1 auto'
    }}
  >
    {children}
  </Box>
);

const getServiceTypePrefix = (serviceType) => {
  if (!serviceType) return '';
  const prefixMap = {
    'hosting': 'Host-',
    'development': 'Dev-',
    'maintenance': 'Maintenance-',
    'design': 'Design-',
    'domain': 'Domain-',
    'application': 'App-',
    'support': 'Support-',
    'seo': 'SEO-',
    'consulting': 'Consulting-',
    'training': 'Training-',
    'license': 'License-',
    'other': ''
  };
  return prefixMap[serviceType] || '';
};

const buildItemName = (it) => {
  const svc = it.service || {};
  const isGenericName = (it.name || '').toLowerCase() === 'service' || (it.name || '').toLowerCase() === 'subscription';
  const baseName = svc.name && (isGenericName || !it.name) ? svc.name : (svc.name || it.name);
  const hasBillingPeriod = svc.billing_period && svc.billing_period !== 'one_time';
  const hasDates = svc.start_date && svc.end_date;
  const isSubscription = hasBillingPeriod && hasDates;

  let periodText = '';
  if (isSubscription) {
    try {
      periodText = ` (${invoiceService.formatDate(svc.start_date)} - ${invoiceService.formatDate(svc.end_date)})`;
    } catch (e) {
      console.warn('Error formatting dates:', e);
    }
  }
  const typePrefix = isSubscription && svc.service_type ? getServiceTypePrefix(svc.service_type) : '';
  return { name: `${typePrefix}${baseName}${periodText}`, baseName };
};

/**
 * Γραμμές για την προεπισκόπηση. Items με ίδιο group_key συγχωνεύονται
 * σε μία γραμμή (τίτλος ομάδας + άθροισμα ποσών).
 */
const buildGroupedRows = (items) => {
  const rows = [];
  const indexByGroup = {};
  (items || []).forEach((it) => {
    const subtotal = (it.unit_price || 0) * (it.quantity || 0);
    const vatAmount = subtotal * ((it.vat_rate || 0) / 100);
    const lineTotal = subtotal + vatAmount;
    const { name: itemName, baseName } = buildItemName(it);
    const groupKey = it.group_key;

    if (groupKey) {
      if (indexByGroup[groupKey] !== undefined) {
        const grouped = rows[indexByGroup[groupKey]];
        grouped.unit_price += subtotal;
        grouped.lineTotal += lineTotal;
        grouped.services.push(baseName);
        return;
      }
      indexByGroup[groupKey] = rows.length;
      rows.push({
        key: groupKey,
        name: it.group_title || itemName,
        quantity: 1,
        unit_price: subtotal,
        vat_rate: it.vat_rate || 0,
        lineTotal,
        services: [baseName],
      });
      return;
    }

    rows.push({
      key: `item-${it.id ?? rows.length}`,
      name: itemName,
      quantity: it.quantity,
      unit_price: it.unit_price,
      vat_rate: it.vat_rate || 0,
      lineTotal,
    });
  });
  return rows;
};

const InvoicePreviewModal = ({ open, onClose, invoiceId }) => {
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [logoSrc, setLogoSrc] = useState('/logo.png');

  useEffect(() => {
    const load = async () => {
      if (!open || !invoiceId) return;
      try {
        setLoading(true);
        setError(null);
        const full = await invoiceService.getInvoice(invoiceId);
        // Enrich item names from related services - always fetch to ensure we have dates
        const items = Array.isArray(full.invoice_items) ? full.invoice_items : [];
        // Get all service IDs that exist
        const serviceIds = Array.from(new Set(items
          .filter(it => it.service_id)
          .map(it => it.service_id)));
        if (serviceIds.length > 0) {
          const fetched = await Promise.all(serviceIds.map(async (id) => {
            const res = await serviceService.getById(id);
            return res.success ? { id, service: res.data } : { id, service: null };
          }));
          const idToService = Object.fromEntries(fetched.map(x => [x.id, x.service]));
          full.invoice_items = items.map(it => {
            const svc = idToService[it.service_id];
            if (svc) {
                  return { 
                    ...it, 
                    service: { 
                      id: it.service_id, 
                      name: svc.name, 
                      start_date: svc.start_date, 
                      end_date: svc.end_date, 
                      billing_period: svc.billing_period,
                      service_type: svc.service_type // Add service_type for prefix
                    } 
                  };
            }
            // If service fetch failed, keep existing service data if available
            return it;
          });
        }
        setInvoice(full);
      } catch (err) {
        console.error('Error loading invoice for preview:', err);
        setError('Αποτυχία φόρτωσης τιμολογίου');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, invoiceId]);

  const handleDownloadPDF = async () => {
    try {
      const pdfBlob = await invoiceService.generateInvoicePDF(invoiceId);
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      const num = invoice?.invoice_number || invoiceId;
      link.download = `Invoice_${num}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
      alert('Αποτυχία λήψης PDF');
    }
  };

  const formatMoney = (v) => invoiceService.formatCurrency(v || 0);
  const formatDate = (d) => (d ? invoiceService.formatDate(d) : '-');
  const paymentMethodLabel = (method) => {
    const map = {
      bank_transfer: 'Κατάθεση σε τράπεζα',
      cash: 'Μετρητά',
      card: 'Κάρτα',
      check: 'Επιταγή',
      other: 'Κατάθεση σε τράπεζα'
    };
    return map[(method || 'other')] || 'Κατάθεση σε τράπεζα';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Προεπισκόπηση Τιμολογίου</Typography>
          <Box>
            <Button onClick={handleDownloadPDF} startIcon={<DownloadIcon />} sx={{ mr: 1 }} disabled={!invoice || loading}>
              PDF
            </Button>
            <Button onClick={onClose} startIcon={<CloseIcon />} color="inherit">Κλείσιμο</Button>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>{error}</Typography>
        )}
        {!invoice ? (
          <Typography variant="body2">Φόρτωση...</Typography>
        ) : (
          <Box sx={{ fontSize: 10, color: '#212529' }}>
            {/* Page margins like PDF (approx) */}
            <Box sx={{ margin: '12px' }}>
              {/* Header */}
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box sx={{ minHeight: 60 }}>
                  <img
                    src={logoSrc}
                    alt="logo"
                    style={{ height: 80 }}
                    onError={() => {
                      if (logoSrc !== '/logo.png') setLogoSrc('/logo.png');
                    }}
                  />
                </Box>
                <SellerBlock />
              </Box>

              {/* Invoice Info */}
              <Box sx={{ border: '1px solid #dee2e6', mb: 2 }}>
                <Box display="flex" p={1} sx={{ borderBottom: '1px solid #dee2e6', background: '#f8f9fa' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Στοιχεία Τιμολογίου</Typography>
                </Box>
                <Box p={1}>
                  <Row left="Τύπος" right={invoiceService.getInvoiceTypeDisplay(invoice.invoice_type).text} />
                  <Row left="Αριθμός" right={invoice.invoice_number || '-'} />
                  <Row left="Ημ. Έκδοσης" right={formatDate(invoice.issue_date)} />
                </Box>
              </Box>

              {/* Client & Payment */}
              <Box sx={{ border: '1px solid #dee2e6', mb: 2 }}>
                <Box display="flex" p={1.2} sx={{ borderBottom: '1px solid #dee2e6', background: '#f8f9fa' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Στοιχεία Πελάτη & Πληρωμής</Typography>
                </Box>
                <Box display="flex" flexWrap="wrap" p={1} gap={2}>
                  <Box sx={{ flex: '1 1 300px', pr: { md: 2 } }}>
                    <Typography sx={{ fontSize: 11 }}>
                      <b>Επωνυμία:</b> {invoice.effective_business_name || invoice.client?.name || '-'}
                    </Typography>
                    {invoice.effective_profession && (
                      <Typography sx={{ fontSize: 11 }}>
                        <b>Επάγγελμα:</b> {invoice.effective_profession}
                      </Typography>
                    )}
                    {invoice.effective_vat_number && (
                      <Typography sx={{ fontSize: 11 }}>
                        <b>ΑΦΜ:</b> {invoice.effective_vat_number}
                      </Typography>
                    )}
                    {invoice.client?.website && (
                      <Typography sx={{ fontSize: 11 }}>
                        <b>Website:</b> {invoice.client.website}
                      </Typography>
                    )}
                    {invoice.client?.email && (
                      <Typography sx={{ fontSize: 11 }}>
                        <b>Email:</b> {invoice.client.email}
                      </Typography>
                    )}
                    {invoice.effective_address && (
                      <Typography sx={{ fontSize: 11 }}>
                        <b>Διεύθυνση:</b> {invoice.effective_address.trim()}
                      </Typography>
                    )}
                    {invoice.effective_city && (
                      <Typography sx={{ fontSize: 11 }}>
                        <b>Πόλη:</b> {invoice.effective_city}
                      </Typography>
                    )}
                    {invoice.effective_country && (
                      <Typography sx={{ fontSize: 11 }}>
                        <b>Χώρα:</b> {invoice.effective_country}
                      </Typography>
                    )}
                    {invoice.effective_tax_office && (
                      <Typography sx={{ fontSize: 11 }}>
                        <b>ΔΟΥ:</b> {invoice.effective_tax_office}
                      </Typography>
                    )}
                    {invoice.effective_postal_code && (
                      <Typography sx={{ fontSize: 11 }}>
                        <b>ΤΚ:</b> {invoice.effective_postal_code}
                      </Typography>
                    )}
                    {invoice.client?.phone && (
                      <Typography sx={{ fontSize: 11 }}>
                        <b>Τηλέφωνο:</b> {invoice.client.phone}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ flex: '1 1 200px' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700 }}>Τρόπος Πληρωμής</Typography>
                    <Typography sx={{ fontSize: 11 }}>{paymentMethodLabel(invoice.payment_method)}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Items Table */}
              <Box sx={{ border: '1px solid #dee2e6', mb: 2 }}>
                <Box component="div" sx={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <Box display="flex" flexWrap="nowrap">
                    <TableCellBox borderTop={false}><Typography sx={{ fontWeight: 600, fontSize: 11 }}>Περιγραφή</Typography></TableCellBox>
                    <TableCellBox borderTop={false} align="center" width={90}><Typography sx={{ fontWeight: 600, fontSize: 11 }}>Ποσότητα</Typography></TableCellBox>
                    <TableCellBox borderTop={false} align="right" width={120}><Typography sx={{ fontWeight: 600, fontSize: 11 }}>Τιμή Μονάδας</Typography></TableCellBox>
                    <TableCellBox borderTop={false} align="right" width={80}><Typography sx={{ fontWeight: 600, fontSize: 11 }}>ΦΠΑ</Typography></TableCellBox>
                    <TableCellBox borderTop={false} align="right" width={120}><Typography sx={{ fontWeight: 600, fontSize: 11 }}>Αξία με ΦΠΑ</Typography></TableCellBox>
                  </Box>
                </Box>
                {buildGroupedRows(invoice.invoice_items).map((row) => (
                  <Box key={row.key} display="flex" flexWrap="nowrap">
                    <TableCellBox>
                      <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{row.name}</Typography>
                    </TableCellBox>
                    <TableCellBox align="center" width={90}>{row.quantity}</TableCellBox>
                    <TableCellBox align="right" width={120}>{formatMoney(row.unit_price)}</TableCellBox>
                    <TableCellBox align="right" width={80}>{row.vat_rate}%</TableCellBox>
                    <TableCellBox align="right" width={120}>{formatMoney(row.lineTotal)}</TableCellBox>
                  </Box>
                ))}
              </Box>

              {/* Totals */}
              <Box display="flex" justifyContent="flex-end">
                <Box sx={{ width: 320, border: '1px solid #dee2e6' }}>
                  <Box sx={{ background: '#f8f9fa', borderBottom: '1px solid #dee2e6', p: 1 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Σύνοψη</Typography>
                  </Box>
                  <Box p={1}>
                    <Row left="Καθαρή Αξία" right={formatMoney(invoice.subtotal ?? 0)} />
                    {invoice.withholding_tax && parseFloat(invoice.withholding_tax) > 0 && (
                      <Row left="Παρακράτηση Φόρου" right={formatMoney(invoice.withholding_tax)} />
                    )}
                    <Row left={`ΦΠΑ : ${invoice.vat_rate || 24}%`} right={formatMoney(invoice.vat_amount ?? 0)} />
                    <Divider sx={{ my: 1 }} />
                    <Row left="Τελικό Ποσό" right={formatMoney(invoice.total_amount ?? 0)} bold />
                  </Box>
                </Box>
              </Box>

              {/* Footer */}
              <Box mt={2} display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography sx={{ fontSize: 11 }}>ΤΡΑΠΕΖΑ: VIVA WALLET</Typography>
                  <Typography sx={{ fontSize: 11 }}>IBAN: GR8870100000000246274537773</Typography>
                  {invoice.notes && (
                    <Box mt={1}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Παρατηρήσεις</Typography>
                      <Typography sx={{ fontSize: 11 }}>{invoice.notes}</Typography>
                    </Box>
                  )}
                </Box>
                {invoice.qr_code && (
                  <Box sx={{ ml: 2, textAlign: 'center' }}>
                    <QRCode value={invoice.qr_code} size={120} />
                    <Typography sx={{ fontSize: 9, mt: 0.5, color: 'text.secondary' }}>
                      QR Code MyData
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Κλείσιμο</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePreviewModal;
